/**
 * Overlay do Detalhe OS 2 — mesmo blur/chrome do V2.
 * Card e dados reais em OrderDetailsMockView.
 *
 * Fecha no clique fora via capture + hit-test do shell (não depende do
 * target cair no backdrop — cobre header / Em aberto / base).
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import '@fontsource-variable/geist/wght.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import '@fontsource/geist-mono/600.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import OrderDetailsMockView from './OrderDetailsMockView';
import './OrderDetailsMockOverlay.css';

/** Acima do mock overlay (100020). */
export const ORDER_DETAILS_MOCK_OVERLAY_Z = 100020;
export const ORDER_DETAILS_MOCK_NESTED_Z = 100050;

const KEEP_CLOSE_SELECTORS = [
  '.order-details-mock-shell',
  '[data-order-details-portal]',
  '.board-order-context-menu',
  '.order-details-mock-tip-wrap',
  '.order-details-mock-tip-popover',
  '.order-details-mock-obs-tooltip',
  '.order-details-mock-anexo-zoom-overlay',
  '.odm-defeito-obs-confirm-overlay',
  '.odm-address-edit-overlay',
  '.open-os-v2-equip-hist-overlay',
  '.open-os-v2-mock-overlay',
  '.os-report-v2-modal',
  '.board-encaminhar-agenda-overlay',
  '.board-encaminhar-backdrop',
  '.odm-orya',
  '.oosv2-agenda-dialog',
];

function isKeepOpenTarget(target) {
  if (!(target instanceof Element)) return false;
  return KEEP_CLOSE_SELECTORS.some((sel) => target.closest(sel));
}

function isPointInElement(el, clientX, clientY) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return (
    clientX >= r.left &&
    clientX <= r.right &&
    clientY >= r.top &&
    clientY <= r.bottom
  );
}

export default function OrderDetailsMockOverlay({
  isOpen,
  onClose,
  order = null,
  statusUptodate = '',
  codstatusRetorno,
  onDadosEquipamento,
  canEncaminharChamado = false,
  onEncaminharChamado,
  encaminharPanel = null,
  onEncaminharPanelClose,
  onEncaminharConfirm,
  onMotivoUpdated,
  onTipoUpdated,
  onAddressUpdated,
  onLocalizacaoUpdated,
  onSolicitanteUpdated,
  isAdminUser = false,
  hasCustomersModule = false,
  onReturnToPendenteSuccess,
  isPreAtendimento: isPreAtendimentoProp,
}) {
  const ignoreBackdropUntilRef = useRef(0);
  const shellRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;

    ignoreBackdropUntilRef.current = Date.now() + 350;

    const closeFromOutside = (e) => {
      if (Date.now() < ignoreBackdropUntilRef.current) return;
      if (typeof e.button === 'number' && e.button !== 0) return;

      // Card / menus / histórico / confirms: não fecha o detalhe
      if (isKeepOpenTarget(e.target)) return;

      // Clique geometricamente sobre o card (mesmo se o target for “estranho”)
      if (isPointInElement(shellRef.current, e.clientX, e.clientY)) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }

      const swallowClick = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        if (typeof ev.stopImmediatePropagation === 'function') {
          ev.stopImmediatePropagation();
        }
        document.removeEventListener('click', swallowClick, true);
      };
      document.addEventListener('click', swallowClick, true);
      window.setTimeout(() => {
        document.removeEventListener('click', swallowClick, true);
      }, 400);

      onCloseRef.current?.();
    };

    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };

    document.addEventListener('mousedown', closeFromOutside, true);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', closeFromOutside, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  if (!isOpen || !order) return null;

  const osId = order.id ?? order.TB02115_CODIGO;
  const title = osId ? `Detalhe OS ${osId}` : 'Detalhe OS';

  return createPortal(
    <div
      className="modal-overlay order-details-modal-overlay order-details-mock-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div ref={shellRef} className="order-details-mock-shell">
        <OrderDetailsMockView
          order={order}
          onClose={onClose}
          statusUptodate={statusUptodate}
          codstatusRetorno={codstatusRetorno}
          onDadosEquipamento={onDadosEquipamento}
          canEncaminharChamado={canEncaminharChamado}
          onEncaminharChamado={onEncaminharChamado}
          encaminharPanel={encaminharPanel}
          onEncaminharPanelClose={onEncaminharPanelClose}
          onEncaminharConfirm={onEncaminharConfirm}
          onMotivoUpdated={onMotivoUpdated}
          onTipoUpdated={onTipoUpdated}
          onAddressUpdated={onAddressUpdated}
          onLocalizacaoUpdated={onLocalizacaoUpdated}
          onSolicitanteUpdated={onSolicitanteUpdated}
          isAdminUser={isAdminUser}
          hasCustomersModule={hasCustomersModule}
          onReturnToPendenteSuccess={onReturnToPendenteSuccess}
          isPreAtendimento={Boolean(isPreAtendimentoProp ?? order?.isPreAtendimento)}
        />
      </div>
    </div>,
    document.body,
  );
}
